import { useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { message } from "antd";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { QUERY_OPC_IMPORT_LOG_STATUS } from "services/graphQL/queries";
import { onSessionTerminating } from "services/session-state";
import {
  clearOpcSync,
  isOpcSyncExpired,
  OPC_SYNC_TIMEOUT_MS
} from "services/opc-sync-state";
import { ImportStatuses } from "utils/utils";
import useTrackedOpcSync from "./use-opc-sync";

// An OPC import runs for tens of seconds to minutes, so the audit feed's 1s
// cadence would buy nothing. This query only exists while a job is outstanding.
const OPC_SYNC_POLL_INTERVAL = 5000;

// useCIQQuery routes every error through ErrorManager on EVERY tick. The toast
// is deduped for 5s by message text, but Sentry.captureException is not — an
// unguarded broken poll would file one event per tick until the cap.
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

const OPC_SYNC_SUCCESS_MESSAGE = "Sync completed successfully";

// Shared with the sync button: a sync can fail before it ever starts (the POST
// is rejected) or after (the job reports FAILED), and the user should not have to
// tell those apart from the wording.
export const OPC_SYNC_FAILURE_MESSAGE =
  "Sync failed due to an unexpected error. Please try again.";

type TOpcImportLog = {
  id: string;
  // Narrowed on the way in, as useGetACCImportLogData does for the submittal
  // log. The column is a plain string on the wire; anything outside the enum is
  // treated as "not terminal yet" below.
  status: ImportStatuses;
};

/**
 * Reports the outcome of an OPC schedule sync, the same way the submittal log
 * reports its own in integration mode: a plain antd toast from the page that
 * owns the sync button, not an app-level notice.
 *
 * Mounted by the Schedules in OPC page and unmounted with it. That is the
 * submittal log's tradeoff too — IntegrationProjectInfo lives in the submittal
 * toolbar, so a sync that lands after the user has navigated away goes
 * unannounced there as well. The handle survives in sessionStorage either way,
 * so returning to the page picks the job back up and reports it then.
 *
 * Renders nothing.
 */
function OpcSyncWatcher() {
  const {
    gqlClientForProject,
    isFeatureFlagEnabled,
    eventLogs
  }: TProjectContext = useContext(ProjectContext);

  const { projectId } = useParams<{ projectId: string }>();

  const handle = useTrackedOpcSync();

  // A terminal status stays in Apollo's result after the handle is cleared, so
  // the announcement is keyed on the row it described.
  const lastAnnouncedRef = useRef<string | null>(null);
  const consecutivePollErrorsRef = useRef(0);

  const isTracking = useMemo(() => {
    if (!handle) return false;
    // The handle survives a project switch (ProjectProvider does not remount on
    // projectId change), so it must not describe another project's sync here.
    if (handle.projectId !== projectId) return false;
    if (!isFeatureFlagEnabled("OPC_INTEGRATION_V1")) return false;
    return !isOpcSyncExpired(handle);
  }, [handle, projectId, isFeatureFlagEnabled]);

  const { data, error, refetch, startPolling, stopPolling } = useCIQQuery<{
    import_log: Array<TOpcImportLog>;
  }>(QUERY_OPC_IMPORT_LOG_STATUS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject || !isTracking,
    pollInterval: OPC_SYNC_POLL_INTERVAL,
    variables: { importLogId: handle?.importLogId }
  });

  // pollInterval in the options AND an imperative startPolling, as the audit
  // poller does — Apollo drops polling when a query is re-observed through a
  // skip toggle, which is exactly what happens here on every sync.
  useEffect(() => {
    if (!isTracking) return undefined;

    startPolling(OPC_SYNC_POLL_INTERVAL);

    // Stop the moment logout begins, so the poll doesn't keep firing (and 401)
    // through the teardown window, and a re-login in this tab cannot resume a
    // job whose token is gone.
    const unsubscribeTerminating = onSessionTerminating(() => {
      stopPolling();
      clearOpcSync();
    });

    return () => {
      unsubscribeTerminating();
      stopPolling();
    };
  }, [isTracking, startPolling, stopPolling]);

  // Wall-clock cap, measured from startedAt so it is correct across a reload.
  // Clears silently: a job that has not reported in fifteen minutes may still
  // land, and "we lost track of your sync" is worse than saying nothing.
  useEffect(() => {
    if (!handle) return undefined;
    if (isOpcSyncExpired(handle)) {
      clearOpcSync();
      return undefined;
    }
    const timer = setTimeout(
      clearOpcSync,
      OPC_SYNC_TIMEOUT_MS - (Date.now() - handle.startedAt)
    );
    return () => clearTimeout(timer);
  }, [handle]);

  useEffect(() => {
    if (!isTracking) return;
    if (!error) {
      consecutivePollErrorsRef.current = 0;
      return;
    }
    consecutivePollErrorsRef.current += 1;
    if (consecutivePollErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
      clearOpcSync();
    }
  }, [error, isTracking]);

  // Free accelerant: the app already polls audit_change_events every second. If
  // the backend emits a row for OPC imports, completion lands sub-second; if it
  // does not, the 5s tick still gets there. Nothing depends on it either way.
  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (
      isTracking &&
      eventLogs.length &&
      previousEventLogs.current !== eventLogs &&
      eventLogs.some(
        (log) =>
          log.data_source === "import_log" && log.project_id === projectId
      )
    ) {
      refetch();
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, isTracking, projectId, refetch]);

  useEffect(() => {
    const row = data?.import_log?.[0];
    if (!row) return;
    // PROCESSING, or any status the backend adds later, means keep waiting. An
    // absent row means the same — it is indistinguishable from "not visible
    // yet", so it must never be read as a failure.
    if (
      row.status !== ImportStatuses.PROCESSED &&
      row.status !== ImportStatuses.FAILED
    ) {
      return;
    }
    if (lastAnnouncedRef.current === row.id) return;

    lastAnnouncedRef.current = row.id;
    clearOpcSync();

    // A plain antd toast, exactly as the submittal log reports its own sync
    // (message.success("Submittal Log imported successfully.") in
    // integration-project-info.tsx). Self-dismissing and unstyled — the "Last
    // synced" line the clear above refreshes is what carries the detail.
    if (row.status === ImportStatuses.PROCESSED) {
      message.success(OPC_SYNC_SUCCESS_MESSAGE);
    } else {
      message.error(OPC_SYNC_FAILURE_MESSAGE);
    }
  }, [data]);

  return null;
}

export default OpcSyncWatcher;
